import { useBus } from "@/core/store/bus";
import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

const STORE_NAME = 'debug'

export type DebugOptions = {
    componentsDebug: boolean
    timeOffset: number
    showPanelButton: boolean
    optimalPerformance: boolean
    disableImages: boolean
    limitProductsPerSection: boolean
}

// ============================================
// Component Performance Debug System
// ============================================

// Простой объект для хранения статистики ререндеров (не реактивный)
const renderStats: Record<string, number> = {}
let statsInterval: NodeJS.Timeout | null = null

// Запуск периодического вывода статистики
const startStatsReporting = (isEnabled: boolean) => {
    if (!isEnabled || process.env.SERVER) return // ✅ Отключаем на сервере

    if (statsInterval) {
        clearInterval(statsInterval)
    }

    statsInterval = setInterval(() => {
        reportAndClearStats()
    }, 4000) // Каждые 4 секунды
}

// Остановка отчетности
const stopStatsReporting = () => {
    if (statsInterval) {
        clearInterval(statsInterval)
        statsInterval = null
    }
}

// Вывод статистики и очистка
const reportAndClearStats = () => {
    const hasStats = Object.keys(renderStats).length > 0

    if (hasStats) {
        console.group('📊 Component Render Statistics (last 4s)')

        // Сортируем по количеству ререндеров
        const sortedStats = Object.entries(renderStats)
            .sort(([, a], [, b]) => b - a)

        sortedStats.forEach(([component, count]) => {
            const emoji = count > 10 ? '🔥' : count > 5 ? '⚠️' : '✅'
            console.log(`${emoji} ${component}: ${count} renders`)
        })

        console.groupEnd()

        // Очищаем статистику
        Object.keys(renderStats).forEach(key => {
            delete renderStats[key]
        })
    }
}

// ============================================
// Pinia Store
// ============================================

export const useDebugStore = defineStore(STORE_NAME, () => {

    const {bus} = useBus()

    const debugAccessEnable = ref(false)
    const componentsDebug = ref(false)

    const options = reactive<DebugOptions>({
        timeOffset: 0,
        componentsDebug: false,
        showPanelButton: false,
        optimalPerformance: false,
        disableImages: false,
        limitProductsPerSection: false
    })

    function setOption<T extends keyof DebugOptions = keyof DebugOptions>(code: T, value: DebugOptions[T]) {
        options[code] = value
    }

    const debugAccessGuard = (cb?: any) => {
        if (!cb) {
            cb = () => {
                bus.emit('router:push', '/dbg-panel')
            }
        }
        if (debugAccessEnable.value) {
            cb()
        } else {
            const pass = prompt("What's your sign?")
            if (pass === '123') {
                debugAccessEnable.value = true
                cb()
            }
        }
    }

    //@ts-ignore
    if (!process.env.SERVER) { // ✅ Hotkey только на клиенте
        bus.on('hotkey:Ctrl+9', () => {
            debugAccessGuard()
        })
    }

    // ============================================
    // Component Performance Debug Methods
    // ============================================

    const enableComponentDebug = () => {
        if (process.env.SERVER) return // ✅ Отключаем на сервере
        
        componentsDebug.value = true
        console.log('🐛 Component Debug Mode Enabled')
        startStatsReporting(true)
    }

    const disableComponentDebug = () => {
        if (process.env.SERVER) return // ✅ Отключаем на сервере
        
        componentsDebug.value = false
        stopStatsReporting()
        console.log('🐛 Component Debug Mode Disabled')
    }

    const incrementRender = (componentName: string) => {
        if (!componentsDebug.value) return

        if (renderStats[componentName]) {
            renderStats[componentName]++
        } else {
            renderStats[componentName] = 1
        }
    }

    const getCurrentStats = () => {
        return {...renderStats}
    }

    const clearStats = () => {
        Object.keys(renderStats).forEach(key => {
            delete renderStats[key]
        })
    }

    const forceReport = () => {
        reportAndClearStats()
    }

    const comp = computed(() => {
    })

    return {
        comp,
        debugAccessEnable,
        debugOptions: options,
        setOption,
        // Component Performance Debug
        get isComponentDebugEnabled() {
            return componentsDebug.value
        },
        enableComponentDebug,
        disableComponentDebug,
        incrementRender,
        getCurrentStats,
        clearStats,
        forceReport,
        debugAccessGuard
    }
}, {
    persist: {
        pick: ['debugAccessEnable', 'debugOptions', 'componentsDebug'],
    }
})

